import { CognitoUserPool, CognitoUser, AuthenticationDetails } from "amazon-cognito-identity-js";
import config from "../config/env";

export const userPool = new CognitoUserPool({
  UserPoolId: config.COGNITO_USER_POOL_ID,
  ClientId: config.COGNITO_CLIENT_ID,
});

// Cognito usernames are e-mails in this pool and the pool is case-insensitive;
// normalizing here keeps localStorage keys stable across sign-ins.
export const normalizeEmail = (email) => (email || "").trim().toLowerCase();

export const makeUser = (email) =>
  new CognitoUser({ Username: normalizeEmail(email), Pool: userPool });

export const makeAuthDetails = (email, password) =>
  new AuthenticationDetails({ Username: normalizeEmail(email), Password: password });

/**
 * Maps the session returned by the SDK to the same `user` shape that
 * react-oidc-context exposed, so existing call sites keep working:
 *   user.id_token, user.access_token, user.refresh_token, user.profile (ID token claims)
 */
export const sessionToUser = (session) => {
  if (!session || !session.isValid()) return null;
  const idToken = session.getIdToken();
  return {
    id_token: idToken.getJwtToken(),
    access_token: session.getAccessToken().getJwtToken(),
    refresh_token: session.getRefreshToken()?.getToken() || null,
    expires_at: idToken.getExpiration(),
    profile: idToken.decodePayload(),
  };
};

/**
 * Translates Cognito SDK errors into pt-BR messages for the UI.
 * Unknown errors fall back to a generic message; the raw error is still
 * logged by the caller for debugging.
 */
export const translateAuthError = (err) => {
  const code = err?.code || err?.name || "";
  switch (code) {
    case "NotAuthorizedException":
      // With PreventUserExistenceErrors=ENABLED an unknown e-mail also lands here.
      return /expired|invalid.*refresh/i.test(err?.message || "")
        ? "Sua sessão expirou. Entre novamente."
        : "E-mail ou senha incorretos.";
    case "UserNotFoundException":
      return "E-mail ou senha incorretos.";
    case "UserNotConfirmedException":
      return "Sua conta ainda não foi confirmada. Verifique o código enviado ao seu e-mail.";
    case "PasswordResetRequiredException":
      return "Você precisa redefinir sua senha antes de entrar.";
    case "InvalidPasswordException":
      return "A senha não atende aos requisitos: mínimo de 8 caracteres, com letra maiúscula, minúscula, número e símbolo.";
    case "InvalidParameterException":
      return "Dados inválidos. Verifique os campos e tente novamente.";
    case "UsernameExistsException":
      return "Já existe uma conta com este e-mail. Tente entrar ou recuperar a senha.";
    case "CodeMismatchException":
      return "Código incorreto. Confira o código recebido por e-mail.";
    case "ExpiredCodeException":
      return "Este código expirou. Solicite um novo código.";
    case "LimitExceededException":
    case "TooManyRequestsException":
    case "TooManyFailedAttemptsException":
      return "Muitas tentativas em pouco tempo. Aguarde alguns minutos e tente novamente.";
    case "NetworkError":
      return "Falha de conexão. Verifique sua internet e tente novamente.";
    default:
      return "Não foi possível concluir a operação. Tente novamente em instantes.";
  }
};
